"use client";
import { MobileDivisionSelector } from "@cmp-src/divisions/DivisionSelector/MobileDivisionSelector";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { Button } from "@cmp-src/primitives/button";
import { Tabs, TabsContent } from "@cmp-src/primitives/tabs";
import { WorkplaceRequestElement } from "@gettactic/api";
import {
  getDefaultDashboardTab,
  getObjectKeys,
  getWeekDates,
  useDisclosure
} from "@gettactic/helpers";
import {
  useAuthenticated,
  useSettings,
  useWeekStartsOn
} from "@gettactic/hooks";
import { format, startOfWeek } from "date-fns";
import React, { useMemo } from "react";
import { OutOfOfficeRequest } from "@/features/dashboard/components/attendance/employee/OutOfOfficeRequest";
import { DashboardDedicatedDeskDetails } from "@/features/dashboard/components/DashboardDedicatedDeskDetails";
import { DashboardEventDetails } from "@/features/dashboard/components/DashboardEventDetails/DashboardEventDetails";
import { DashboardVisitDetails } from "@/features/dashboard/components/DashboardVisitDetails";
import { LocationChangeReservationsModal } from "@/features/dashboard/components/LocationChangeReservationsModal";
import { MobileDayStrip } from "@/features/dashboard/components/mobile/MobileDayStrip";
import { MobilePeopleSection } from "@/features/dashboard/components/mobile/MobilePeopleSection";
import { MobileQuickActions } from "@/features/dashboard/components/mobile/MobileQuickActions";
import { MobileSelectedDayDetail } from "@/features/dashboard/components/mobile/MobileSelectedDayDetail";
import { MobileContentLayoutContainer } from "@/features/dashboard/components/mobile/mobile/MobileContentLayoutContainer";
import { DashboardTabsList } from "@/features/dashboard/components/partial/DashboardTabsList";
import { LiveMap } from "@/features/dashboard/components/subpages/LiveMap";
import { News } from "@/features/dashboard/components/subpages/News";
import { SiteInfo } from "@/features/dashboard/components/subpages/SiteInfo";
import { Visitors } from "@/features/dashboard/components/subpages/Visitors";
import { PageOptions } from "@/features/dashboard/DashboardStateContext";
import { useDashboardData } from "@/features/dashboard/hooks/useDashboardData";
import { isDedicatedDeskSelection } from "@/features/dashboard/lib/dedicatedDeskSelection";
import { RequestDetailsPage } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsPage";
import { TeamSchedule } from "@/features/teams/components/TeamSchedule";
import { scheduleToOfficeWallClock } from "@/lib/scheduleToOfficeWallClock";
import {
  useWorkplaceRequestModal,
  WorkplaceRequestModalProvider
} from "../contexts/WorkplaceRequestModalContext";

const isVisit = (event: unknown): boolean => {
  return !!event && typeof event === "object" && "visitor" in event;
};

function MobileDashboardContent() {
  const { isOfficePrivacyEnabled } = useSettings();
  const {
    userContext: {
      currentOffice: authCurrentOffice,
      authenticatedUser: authUser
    }
  } = useAuthenticated();
  const isOrgPrivacyEnabled = isOfficePrivacyEnabled(authCurrentOffice?.id);
  const defaultTab: PageOptions = useMemo(
    () => getDefaultDashboardTab(isOrgPrivacyEnabled, true, authUser.slug),
    [isOrgPrivacyEnabled, authUser.slug]
  );
  const {
    shouldRenderContent,
    activeTab,
    handleTabChange,
    setOffices,
    selectedEvent,
    setSelectedEvent,
    isAdmin,
    hasPages,
    nextWeek,
    prevWeek,
    currentOffice,
    locationData,
    finalDaysCount,
    pendingLocationChange,
    dismissPendingLocationChange,
    oooRequest,
    setOooRequest,
    weekDate,
    setPage
  } = useDashboardData(
    // on Mobile we always show Attendance tab
    // because user's own info is also on it
    { defaultTab }
  );

  const weekStartsOn = useWeekStartsOn();
  const hideWeekends =
    authUser.organization?.features?.includes("hide.weekends") ?? false;

  // Index of today within the rendered week days, which start on the
  // configured day and may exclude weekends
  const todayIndex = React.useMemo(() => {
    const today = new Date();
    const weekDays = getWeekDates(
      startOfWeek(today, { weekStartsOn }),
      hideWeekends
    );
    const todayStr = format(today, "yyyy-MM-dd");
    const index = weekDays.findIndex(
      (day) => format(day, "yyyy-MM-dd") === todayStr
    );
    // Today can be a hidden weekend day; fall back to the first day
    return Math.max(index, 0);
  }, [weekStartsOn, hideWeekends]);

  const [selectedDayIndex, setSelectedDayIndex] =
    React.useState<number>(todayIndex);

  // The week start setting resolves asynchronously; re-anchor the selection
  // to today if it changes after mount
  React.useEffect(() => {
    setSelectedDayIndex(todayIndex);
  }, [todayIndex]);
  const eventDetailsDisclosure = useDisclosure();
  const {
    isOpen: workplaceRequestsModalOpen,
    selectedRequest,
    closeModal
  } = useWorkplaceRequestModal();

  React.useEffect(() => {
    if (selectedEvent) {
      eventDetailsDisclosure.onOpen();
    }
  }, [selectedEvent]);

  const days = useMemo(
    () => getObjectKeys(locationData.scheduleMeData.data?.days || {}),
    [locationData.scheduleMeData.data?.days]
  );

  // The agenda endpoint returns true-UTC times, while DashboardEventDetails
  // reads the office wall-clock "fake Z" convention of the schedules
  // endpoints — the same normalisation AgendaDay's edit flow does [sc-9350].
  const selectedSchedule = useMemo(
    () =>
      selectedEvent &&
      !isVisit(selectedEvent) &&
      !isDedicatedDeskSelection(selectedEvent)
        ? scheduleToOfficeWallClock(
            selectedEvent,
            selectedEvent.office?.time_zone
          )
        : null,
    [selectedEvent]
  );

  if (!shouldRenderContent) {
    return null;
  }

  return (
    <>
      <MobileContentLayoutContainer cleanWrapper>
        <Tabs
          defaultValue="schedules"
          value={activeTab}
          onValueChange={handleTabChange}
          className="flex w-screen flex-col"
        >
          <div className={"mt-2 flex items-center gap-2 pl-2"}>
            <div className={"min-w-0 flex-1"}>
              <MobileDivisionSelector
                selectedOffice={currentOffice}
                onSelectOffice={(office) => {
                  if (!office) return;
                  setOffices([office.id]);
                }}
              />
            </div>
            <div>
              <DashboardTabsList
                hasPages={hasPages}
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                isMobile={true}
                showAttendanceTab={!isOrgPrivacyEnabled}
                setPage={setPage}
                defaultPage={defaultTab}
              />
            </div>
          </div>

          {/* Schedules tab content */}
          <TabsContent value="schedules" asChild>
            <div className="flex flex-col gap-4 pt-3">
              {/* Quick actions */}
              <MobileQuickActions />

              {/* Day strip */}
              <MobileDayStrip
                weekDate={weekDate}
                onPrevWeek={prevWeek}
                onNextWeek={nextWeek}
                days={days as string[]}
                selectedDayIndex={selectedDayIndex}
                onSelectDay={setSelectedDayIndex}
                agenda={locationData.scheduleMeData.data?.agenda}
              />

              {/* Selected day detail */}
              <MobileSelectedDayDetail
                locationData={locationData}
                selectedDayIndex={selectedDayIndex}
              />

              {/* People section */}
              {!isOrgPrivacyEnabled && (
                <MobilePeopleSection
                  daysCount={finalDaysCount}
                  selectedDayIndex={selectedDayIndex}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="liveMap" asChild>
            <div className="px-4 pt-3">
              <LiveMap />
            </div>
          </TabsContent>

          <TabsContent value="visitors" asChild>
            <div className="px-4 pt-3">
              <Visitors />
            </div>
          </TabsContent>

          <TabsContent value="news" asChild>
            <div className="px-4 pt-3">
              <News />
            </div>
          </TabsContent>

          <TabsContent value="siteInfo" asChild>
            <div className="px-4 pt-3">
              <SiteInfo />
            </div>
          </TabsContent>
        </Tabs>
      </MobileContentLayoutContainer>

      <OutOfOfficeRequest
        isOpen={!!oooRequest}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOooRequest(null);
            locationData.invalidateQueries();
          }
        }}
        request={oooRequest}
      />
      <LocationChangeReservationsModal
        pending={pendingLocationChange}
        onClose={dismissPendingLocationChange}
      />

      <MobileDrawer
        isOpen={workplaceRequestsModalOpen}
        onOpenChange={closeModal}
        scrollableContent={true}
      >
        <RequestDetailsPage
          requestId={selectedRequest?.id}
          isAdmin={isAdmin}
          onRequestUpdate={(_updatedRequest: WorkplaceRequestElement) => {
            closeModal();
          }}
          onClose={closeModal}
          initialRequest={selectedRequest}
          dontTouchUrl={true}
          isPageMode={true}
          isInMobileDrawer={true}
        />
      </MobileDrawer>
      <MobileDrawer
        isOpen={eventDetailsDisclosure.isOpen}
        size={"fit"}
        onOpenChange={eventDetailsDisclosure.onOpenChange}
        onVisibleChange={(isVisible, isOpen) => {
          if (!isVisible && !isOpen) setSelectedEvent(null);
        }}
        below={
          <div className={"w-full"}>
            <Button
              className={"w-full"}
              variant={"secondary"}
              onClick={eventDetailsDisclosure.close}
            >
              Cancel
            </Button>
            <div className={"h-4"} />
          </div>
        }
        scrollableContent
        contentUnderGrabber
      >
        {selectedEvent && isVisit(selectedEvent) ? (
          <DashboardVisitDetails visit={selectedEvent} />
        ) : isDedicatedDeskSelection(selectedEvent) ? (
          <DashboardDedicatedDeskDetails
            desk={selectedEvent.desk}
            date={selectedEvent.date}
          />
        ) : selectedSchedule ? (
          <DashboardEventDetails
            schedule={selectedSchedule}
            onCancel={eventDetailsDisclosure.close}
            isOrgPrivacyEnabled={isOrgPrivacyEnabled}
            teamSchedule={
              !isOrgPrivacyEnabled && (
                <div className={"mb-4 flex flex-col gap-2"}>
                  <TeamSchedule
                    daysCount={finalDaysCount}
                    selectedDayIndex={selectedDayIndex}
                    showAsMobileDaily
                  />
                </div>
              )
            }
          />
        ) : null}
      </MobileDrawer>
    </>
  );
}

export function MobileDashboard() {
  return (
    <WorkplaceRequestModalProvider>
      <MobileDashboardContent />
    </WorkplaceRequestModalProvider>
  );
}
